import { useContext } from 'react';
import PropTypes from 'prop-types';
import { Redirect, Route } from 'react-router-dom';

import Loader from '../../juristec-ui/core/Loader';

import { AuthContext } from '../../context/AuthProvider';

const logoutRedirect = () => {
  let goto = window.location.pathname;
  if (goto !== '/') goto = `?to=${goto}`;
  window.location.replace(`${window.location.protocol}//${window.location.host}/login${goto}`);
};

const PrivateRoute = ({
  component: RouteComponent,
  onlyAdmin = false,
  onlyScientist = false,
  onlySuper = false,
  adminSupport = false,
  userAttributes = undefined,
  companyAttributes = undefined,
  ...rest
}) => {
  const {
    currentUser, user, claimsUser, userCompany,
  } = useContext(AuthContext);

  const checkAttributes = () => ((
    userAttributes && !!Object.keys(userAttributes).find((k) => (
      user[k] !== userAttributes[k]
    ))
  ) || (
    companyAttributes && !!Object.keys(companyAttributes).find((k) => (
      userCompany[k] !== companyAttributes[k]
    ))
  ));

  const filterRoute = (routeProps, condition) => {
    if (!currentUser) return logoutRedirect();
    if (!user || !userCompany) return <Loader />;
    const params = new URLSearchParams(window.location.search);
    const redirectTo = params.get('to') ?? '/home';
    if (!condition) return <Redirect to={redirectTo} />;
    if (checkAttributes()) return <Redirect to={redirectTo} />;
    return <RouteComponent {...routeProps} />;
  };

  const filterIsScientist = (routeProps) => (
    filterRoute(routeProps, user?.isScientist || ['support', 'scientist', 'super'].includes(user?.role))
  );

  const filterIsSuper = (routeProps) => (
    filterRoute(routeProps, user?.isScientist || ['support', 'super'].includes(user?.role))
  );

  const filterIsAdmin = (routeProps) => (
    filterRoute(routeProps, claimsUser?.is_admin)
  );

  const filterIsAdminOrSupport = (routeProps) => (
    filterRoute(routeProps, claimsUser?.is_admin || claimsUser?.is_support)
  );

  if (onlyAdmin) {
    return (
      <Route
        {...rest}
        render={filterIsAdmin}
      />
    );
  }

  if (onlyScientist) {
    return (
      <Route
        {...rest}
        render={filterIsScientist}
      />
    );
  }

  if (onlySuper) {
    return (
      <Route
        {...rest}
        render={filterIsSuper}
      />
    );
  }

  if (adminSupport) {
    return (
      <Route
        {...rest}
        render={filterIsAdminOrSupport}
      />
    );
  }

  return (
    <Route
      {...rest}
      render={(routeProps) => (currentUser ? (
        <RouteComponent {...routeProps} />
      ) : (
        logoutRedirect()
      ))}
    />
  );
};

PrivateRoute.propTypes = {
  component: PropTypes.func.isRequired,
  onlyAdmin: PropTypes.bool,
  onlyScientist: PropTypes.bool,
  onlySuper: PropTypes.bool,
  adminSupport: PropTypes.bool,
  userAttributes: PropTypes.objectOf(PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.string,
    PropTypes.number,
  ])),
  companyAttributes: PropTypes.objectOf(PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.string,
    PropTypes.number,
  ])),
};

export default PrivateRoute;
